import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import { supabase } from '../config/supabase';

const AuthContext = createContext({});

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
};

// Global flag to prevent multiple initializations across renders
let isGloballyInitializing = false;
let hasInitialized = false;

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [session, setSession] = useState(null);
    const [loading, setLoading] = useState(true);
    const mountedRef = useRef(true);

    const fetchUserProfile = useCallback(async (userId) => {
        try {
            const { data: userData } = await supabase
                .from('users')
                .select('role, full_name')
                .eq('id', userId)
                .single();
            
            return userData;
        } catch (error) {
            console.error('Error fetching user profile:', error);
            return null;
        }
    }, []);

    useEffect(() => {
        mountedRef.current = true;

        // Prevent multiple initializations
        if (isGloballyInitializing || hasInitialized) {
            setLoading(false);
            return;
        }
        
        isGloballyInitializing = true;

        const initAuth = async () => {
            try {
                const { data: { session: currentSession } } = await supabase.auth.getSession();
                
                if (!mountedRef.current) return;
                
                setSession(currentSession);
                
                if (currentSession?.user) {
                    const userData = await fetchUserProfile(currentSession.user.id);
                    
                    if (!mountedRef.current) return;
                    
                    setUser({
                        id: currentSession.user.id,
                        email: currentSession.user.email,
                        role: userData?.role || 'user',
                        full_name: userData?.full_name || currentSession.user.email
                    });
                }
                
                hasInitialized = true;
            } catch (error) {
                console.error('Error initializing auth:', error);
            } finally {
                isGloballyInitializing = false;
                if (mountedRef.current) {
                    setLoading(false);
                }
            }
        };

        initAuth();

        // Listen for auth changes - with debounce to prevent race conditions
        let timeoutId = null;
        const { data: { subscription } } = supabase.auth.onAuthStateChange(
            async (event, newSession) => {
                if (!mountedRef.current) return;
                
                // Skip INITIAL_SESSION event since we handle it above
                if (event === 'INITIAL_SESSION') return;
                
                // Debounce rapid auth state changes
                if (timeoutId) clearTimeout(timeoutId);
                
                timeoutId = setTimeout(async () => {
                    if (!mountedRef.current) return;
                    
                    console.log('Auth state changed:', event);
                    
                    setSession(newSession);
                    
                    if (newSession?.user) {
                        const userData = await fetchUserProfile(newSession.user.id);
                        
                        if (!mountedRef.current) return;
                        
                        setUser({
                            id: newSession.user.id,
                            email: newSession.user.email,
                            role: userData?.role || 'user',
                            full_name: userData?.full_name || newSession.user.email
                        });
                    } else if (event === 'SIGNED_OUT') {
                        setUser(null);
                        setSession(null);
                    }
                }, 100);
            }
        );

        return () => {
            mountedRef.current = false;
            if (timeoutId) clearTimeout(timeoutId);
            subscription?.unsubscribe();
        };
    }, [fetchUserProfile]);

    // Sign up
    const signUp = async (email, password, fullName) => {
        const { data, error } = await supabase.auth.signUp({
            email,
            password,
            options: {
                data: {
                    full_name: fullName,
                    role: 'user'
                },
                emailRedirectTo: 'https://pharmaempower.com/login'
            }
        });

        if (error) throw error;

        // Insert into users table
        if (data?.user) {
            await supabase
                .from('users')
                .insert([{
                    id: data.user.id,
                    full_name: fullName,
                    email: email,
                    role: 'user'
                }]);
        }

        return data;
    };

    // Sign in
    const signIn = async (email, password) => {
        const { data, error } = await supabase.auth.signInWithPassword({
            email,
            password
        });

        if (error) throw error;
        return data;
    };

    // Sign out
    const signOut = async () => {
        const { error } = await supabase.auth.signOut();
        if (error) throw error;
        setUser(null);
        setSession(null);
    };

    // Get current token
    const getToken = async () => {
        const { data: { session } } = await supabase.auth.getSession();
        return session?.access_token || null;
    };

    // Check if user is admin
    const isAdmin = () => {
        return user?.role === 'admin';
    };

    const value = {
        user,
        session,
        loading,
        signUp,
        signIn,
        signOut,
        getToken,
        isAdmin,
        supabase
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthContext;
